// Content for Scatura Solutions - an intelligent automation, resilience & modernization studio.

export const COMPANY = {
  name: 'Scatura',
  fullName: 'Scatura Solutions',
  tagline: 'Resilience engineered. Reliability delivered.',
  email: 'hello@scaturasolutions.com.au',
  phone: '+61 0423 103 666',
  address: 'Level 27, 101 Collins St, Melbourne VIC 3000',
  location: 'Level 27, 101 Collins St, Melbourne VIC 3000',
  website: 'scaturasolutions.com.au',
};

export const NAV_LINKS = [
  { label: 'Services', href: '#services' },
  { label: 'Consulting', href: '#consulting' },
  { label: 'Approach', href: '#approach' },
  { label: 'Training', href: '#training' },
  { label: 'Blog', href: '/blog' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export const HERO_METRICS = [
  { value: '40+', label: 'Automations delivered' },
  { value: '99.98%', label: 'Uptime on prior projects' },
  { value: '<15m', label: 'MTTR track record' },
];

export const DOMAINS = [
  'Banking & Financial Services',
  'Insurance',
  'Telecommunications',
  'Public Sector',
  'Retail & E-commerce',
  'Healthcare',
  'Logistics',
  'Energy & Utilities',
];

export const CASE_STUDIES = [
  {
    id: 'cs1',
    domain: 'Self-Healing Systems',
    accent: 'from-emerald-500 to-teal-500',
    title: 'Dead Letter Queue auto-cleanup',
    problem:
      'A high-throughput messaging platform kept piling up dead-letter queues during peak load. Each pile-up woke L1 engineers up to manually inspect, purge and replay messages.',
    outcome:
      'Built a self-healing DLQ workflow that detects pile-ups, applies policy-based auto-cleanup and replays recoverable messages - only genuinely stuck messages ever escalate to a human.',
    metric: 'Zero',
    metricLabel: 'L1 pages for DLQ pile-ups',
  },
  {
    id: 'cs2',
    domain: 'Intelligent Automation',
    accent: 'from-indigo-500 to-violet-500',
    title: 'IBM MFT customer onboarding',
    problem:
      'Onboarding a new customer to IBM Managed File Transfer took engineers 1-2 weeks of manual work - server config, certificates, agent setup, routing rules, testing.',
    outcome:
      'Delivered an Ansible-driven onboarding pipeline with a self-service front-end. New MFT partners now provision themselves end-to-end with certificate exchange and validation in minutes.',
    metric: '5-10 min',
    metricLabel: 'Down from 1-2 weeks',
  },
  {
    id: 'cs3',
    domain: 'Chaos Engineering',
    accent: 'from-cyan-500 to-blue-500',
    title: 'Business-hours availability failures',
    problem:
      'A revenue-critical application was silently degrading during business hours. Standard test suites ran only in staging and never surfaced the real production failure modes.',
    outcome:
      'Designed and ran controlled chaos experiments against production-like environments. Exposed multiple critical latent bugs impacting availability during business hours - all fixed before customers were impacted.',
    metric: 'Critical bugs',
    metricLabel: 'Caught before customers noticed',
  },
];

export const SERVICES = [
  {
    id: 'automation',
    title: 'Intelligent Automation',
    tagline: 'From napkin sketch to production-quality POC in weeks.',
    description:
      'We prove value fast with production-quality POCs spanning Ansible-driven infrastructure automation, DevOps pipelines, and Microsoft Power Platform (Power Automate, Power Apps) workflows - all secured end-to-end with HashiCorp Vault, secret rotation and least-privilege identity patterns.',
    icon: 'Sparkles',
    bullets: [
      'Ansible playbooks & DevOps pipeline automation',
      'Power Platform (Power Automate, Power Apps) POCs',
      'LLM copilots, event-driven orchestration (Temporal, Kafka)',
      'HashiCorp Vault, secret rotation & zero-trust identity',
    ],
    accent: 'from-indigo-500 to-violet-500',
  },
  {
    id: 'chaos',
    title: 'Chaos Engineering',
    tagline: 'Break your systems on purpose - before customers do.',
    description:
      'Hands-on chaos experience across Azure Chaos Studio and Litmus Chaos, with proven POCs, automated fault-injection testing and onboarding playbooks for AKS-based infrastructure. GameDays, resilience scorecards and runbooks your on-call engineers will actually use.',
    icon: 'Waves',
    bullets: [
      'Azure Chaos Studio POCs, automated testing & team onboarding',
      'Litmus Chaos POCs & testing for AKS / Kubernetes workloads',
      'GameDay design, failure-injection & continuous verification',
      'Resilience scorecards & maturity roadmap',
    ],
    accent: 'from-cyan-500 to-blue-500',
  },
  {
    id: 'selfheal',
    title: 'Self-Healing Systems',
    tagline: 'Eliminate L1 toil. Let the systems recover themselves.',
    description:
      'We integrate your monitoring stack and AIOps tools with automated recovery workflows and ticketing systems (ServiceNow, Jira, PagerDuty) so repeated alerts trigger automated triage, remediation and ticket lifecycle updates - not a 3am page for an L1 engineer.',
    icon: 'Activity',
    bullets: [
      'Monitoring + AIOps integration (Azure Monitor, Dynatrace, Datadog, Moogsoft)',
      'Automated triage & recovery for recurring alerts and events',
      'ServiceNow / Jira / PagerDuty closed-loop ticket automation',
      'Eliminate L1 manual intervention with policy-guarded runbooks',
    ],
    accent: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'modernization',
    title: 'App Modernization',
    tagline: 'Legacy in. Modern out. POC first, always.',
    description:
      'We deep-dive into your legacy systems, understand the constraints, and propose a modernization path on your preferred cloud - be it cloud-native re-architecture, rehost / lift-and-shift, or targeted refactor. Every recommendation is backed by a working POC so decisions are made on evidence, not slideware.',
    icon: 'Boxes',
    bullets: [
      'Legacy system discovery & modernization strategy (6 Rs)',
      'Cloud-native, rehost, refactor and replatform POCs',
      'Preferred-cloud delivery on Azure, AWS or GCP',
      'Kubernetes, serverless & platform engineering',
    ],
    accent: 'from-fuchsia-500 to-pink-500',
  },
  {
    id: 'training',
    title: 'Coaching, Workshops & Hackathons',
    tagline: 'Real problems. Real cohorts. Real-world outcomes.',
    description:
      'Real problem-solving sessions built around your actual incidents and backlog. Chaos simulation workshops, Ansible use-case hackathons scoped to customer use cases, and long-form coaching to upskill your teams - delivered directly or in partnership with vendor training programs.',
    icon: 'GraduationCap',
    bullets: [
      'Real problem-solving sessions on your live incidents & backlog',
      'Chaos simulation workshops & GameDays',
      'Ansible use-case hackathons tailored to customer scenarios',
      'Upskilling coaching, delivered directly or via vendor training partners',
    ],
    accent: 'from-orange-500 to-amber-500',
  },
];

export const APPROACH = [
  {
    id: 'discover',
    step: '01',
    title: 'Discover',
    description:
      'A short, sharp diagnostic. We map your systems, incident history and business-critical journeys - no fluff, no 80-page decks.',
    icon: 'Search',
  },
  {
    id: 'prove',
    step: '02',
    title: 'Prove',
    description:
      'We ship a working proof of concept against your real systems, so decisions are made on evidence - not slideware.',
    icon: 'Rocket',
  },
  {
    id: 'scale',
    step: '03',
    title: 'Scale',
    description:
      'We productionize what works: observability, guardrails, SLOs and runbooks that survive the on-call rotation.',
    icon: 'TrendingUp',
  },
  {
    id: 'enable',
    step: '04',
    title: 'Enable',
    description:
      'We transfer ownership through workshops, pairing and coaching - so your team runs the show long after we\u2019re gone.',
    icon: 'Users',
  },
];

export const STATS = [
  { value: '40+', label: 'Automation POCs shipped' },
  { value: '12', label: 'Industries served' },
  { value: '99.98%', label: 'Avg. production uptime' },
  { value: '4.9/5', label: 'Post-engagement CSAT' },
];

export const TRAINING_TRACKS = [
  {
    id: 'chaos',
    duration: '2 days',
    format: 'On-site or remote',
    title: 'Chaos Engineering Bootcamp',
    description:
      'Design and run your first GameDay end-to-end. Failure taxonomy, blast-radius controls, and a resilience scorecard your leadership will actually read.',
    outcomes: ['Ship your first GameDay', 'Failure hypothesis library', 'Resilience maturity scorecard'],
    icon: 'Zap',
  },
  {
    id: 'automation',
    duration: '3 days',
    format: 'Team hackathon',
    title: 'Intelligent Automation Hackathon',
    description:
      'Cross-functional teams pick a real bottleneck and ship a working LLM- or event-driven automation POC by day three. Winners get funded.',
    outcomes: ['3-5 working POCs per cohort', 'Reusable automation patterns', 'Executive demo & funding plan'],
    icon: 'Cpu',
  },
  {
    id: 'sre',
    duration: '4 weeks',
    format: 'Embedded coaching',
    title: 'SRE & Self-Healing Coaching',
    description:
      'A staff engineer pairs with your team to introduce SLOs, error budgets and closed-loop remediation - tuned to your stack and incident patterns.',
    outcomes: ['SLO/SLI catalogue', 'Auto-remediation playbooks', 'On-call runbook rewrite'],
    icon: 'HeartPulse',
  },
];

export const WHY_CHOOSE_US = [
  {
    title: 'Outcomes, not decks',
    description: 'We measure success in shipped POCs, recovered incidents, and calm on-call rotations - not slideware.',
    icon: 'Target',
  },
  {
    title: 'Senior-only delivery',
    description: 'Every engagement is led by senior engineers who have actually run large-scale systems at scale. No bench warmers, no pyramid staffing.',
    icon: 'BadgeCheck',
  },
  {
    title: 'Left-behind capability',
    description: 'We embed with your teams and transfer ownership from day one. When we leave, you\u2019re stronger, not dependent.',
    icon: 'Handshake',
  },
  {
    title: 'Pragmatic, opinionated',
    description: 'We\u2019ve seen what works. We pick the smallest thing that will change your operating model - and we ship it.',
    icon: 'Compass',
  },
];

// Legacy testimonials array kept for backwards compatibility (no longer rendered).
export const TESTIMONIALS = [];

export const FAQS = [
  {
    id: 'q1',
    question: 'How do engagements usually start?',
    answer:
      'Almost always with a 2-week paid diagnostic. We map your systems, incident history and top revenue-critical journeys, then propose a shaped engagement with clear success metrics.',
  },
  {
    id: 'q2',
    question: 'What does a typical POC look like?',
    answer:
      'A 3-6 week, timeboxed build against your real systems. You get working code, a demo, honest cost & risk analysis, and a productionization plan - not a slide deck.',
  },
  {
    id: 'q3',
    question: 'Do you work with regulated industries?',
    answer:
      'Yes. We\u2019ve delivered for fintech, healthcare and public-sector clients. We work within your compliance boundary and can operate under an existing DPA, SOC 2 controls, or air-gapped review.',
  },
  {
    id: 'q4',
    question: 'How is training different from a webinar?',
    answer:
      'Every workshop and hackathon is hands-on against a real problem in your environment. Attendees leave with working code, playbooks and a scorecard - not just certificates.',
  },
  {
    id: 'q5',
    question: 'What does pricing look like?',
    answer:
      'We offer fixed-scope diagnostics, milestone-based POCs, monthly retainers for embedded coaching, and per-cohort training packages. Reach out for a tailored quote after a discovery call.',
  },
  {
    id: 'q6',
    question: 'Where are you based?',
    answer:
      'Based in Melbourne, Australia - but we deliver worldwide. Our core team operates from Melbourne with remote engagements across APAC, EMEA and North America. For workshops and hackathons, we happily travel to your office.',
  },
];


export const CONSULTING_PRACTICES = [
  {
    id: 'infra-it',
    title: 'Infra & IT Consulting',
    tagline: 'Platform engineering, Azure-specialised.',
    description:
      'Platform engineering advisory focused on automation, self-healing, self-service portals, architecture patterns and hands-on coaching. Deep specialisation in Microsoft Azure landing zones, AKS, Bicep, Terraform and DevOps toolchains.',
    bullets: [
      'Automation, self-healing and self-service platforms',
      'Reference architecture patterns and blueprints',
      'Azure landing zones, AKS, Bicep, Terraform, DevOps',
      'Team coaching and opinionated tooling choices',
    ],
    icon: 'Server',
    accent: 'from-sky-500 to-indigo-500',
  },
  {
    id: 'payments',
    title: 'Digital Payments Consulting',
    tagline: 'End-to-end digital payment solutions.',
    description:
      'Innovative payment solutions and full operational uplift across the digital payments lifecycle: daily processing, reconciliation, settlement, dispute and exception management, fraud and risk monitoring, compliance, and call-centre workflows. Built to strengthen customer trust and regulatory excellence.',
    bullets: [
      'Daily processing, reconciliation and settlement',
      'Dispute and exception management workflows',
      'Fraud, risk monitoring and compliance controls',
      'Call-centre enablement and customer experience',
    ],
    icon: 'CreditCard',
    accent: 'from-amber-500 to-orange-500',
  },
];

export const FOUNDER = {
  name: 'Sandhya Anoop Mohanan',
  role: 'Founder & Principal Engineer',
  location: 'Melbourne, Australia',
  photo: '/founder.jpg',
  bio: 'Infrastructure and automation leader with close to 20 years in IT. Drives enterprise automation strategy across cloud, platform and operations for global enterprises - equally at home shaping multi-year roadmaps and diagnosing production-critical systems hands-on.',
  bioExtended: '',
  highlights: [
    'Enterprise automation programs with measurable efficiency gains',
    'AI-driven ops on Microsoft Copilot Studio & Azure AI Foundry',
    '5+ years Azure PaaS, IaaS & Chaos Studio; hands-on chaos engineering',
    '15+ years middleware ops - Oracle WebLogic & IBM WebSphere',
  ],
  linkedin: 'https://www.linkedin.com/in/sandhya-anoop',
};

